import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Pagination,
  Typography,
} from "@mui/material";
import { Button } from "@mui/material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useNavigate } from "react-router-dom";
import { formatNumber, toIntPercent, formatDuration } from "../../utils/constant";
import { Skeleton } from "@mui/material";

export const TopCustomersTable = ({
  tableLoading,
  data = [],
  totalCount = 0,
  page = 1,
  rowsPerPage = 5,
  onPageChange,
}) => {
  const navigate = useNavigate();

  const handleRowClick = (row) => {
    navigate(`/dashboard/${encodeURIComponent(row.companyName)}`, {
      state: {
        status: row.status,
        apisSubscribed: row.apisSubscribed,
      },
    });
  };

  const TableSkeletonRows = ({ rows = 5 }) => {
    return (
      <>
        {Array.from({ length: rows }).map((_, index) => (
          <TableRow key={index}>
            <TableCell><Skeleton width={140} /></TableCell>
            <TableCell><Skeleton width={60} height={24} /></TableCell>
            <TableCell><Skeleton width={40} /></TableCell>
            <TableCell><Skeleton width={80} /></TableCell>
            <TableCell><Skeleton width={60} /></TableCell>
            <TableCell><Skeleton width={80} /></TableCell>
            <TableCell><Skeleton width={90} /></TableCell>
          </TableRow>
        ))}
      </>
    );
  };
  
  return (
    <Box>
      <Paper sx={{ width: "100%", borderRadius: 3, boxShadow: 2, p: 2, pb: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          {/* Left side: Title + subtitle */}
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Top Customers
            </Typography>

            <Typography variant="body2" color="text.secondary" 
              sx={{
                mb: 0,
                fontWeight: 400,
                fontSize: "1.4rem",
              }}>
              Highest API usage across the platform
            </Typography>
          </Box>

          {/* Right side: Button */}
          <Button
            variant="outlined"
            endIcon={<ArrowOutwardIcon fontSize="small" />}
            onClick={() => navigate("/customer-analytics")}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              px: 2,
              py: 0.75,
              borderColor: "#E2E8F0",
              color: "#0F172A",
              "&:hover": {
                backgroundColor: "#F8FAFC",
                borderColor: "#CBD5E1",
              },
            }}
          >
            View All Customers
          </Button>
        </Box>
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                {[
                  "Company",
                  "Status",
                  "APIs",
                  "Total Calls",
                  "Success Rate",
                  "Credits Used",
                  "Avg Response",
                ].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#64748B",
                      borderBottom: "1px solid #E2E8F0",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
            {tableLoading ? (
                <TableSkeletonRows rows={rowsPerPage} />
              ) : data.length > 0 ? (
                data.map((row, index) => (
                  <TableRow
                    hover
                    onClick={() => handleRowClick(row)}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#F8FAFC",
                      },
                    }}
                  >    
                  <TableCell
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/customer-analytics/api-logs/${encodeURIComponent(row.companyName)}`);
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "primary.main",
                        borderBottom: "1px solid",
                        borderBottomColor: "primary.main",
                        display: "inline-block",
                      }}
                    >
                      {row.companyName}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={row.status}
                      size="small"
                      sx={{
                        textTransform: "capitalize",
                        fontWeight: 700,
                        fontSize: "11px",
                        backgroundColor:
                          row.status === "Active"
                            ? "#DCFCE7"
                            : "#E5E7EB",
                        color:
                          row.status === "Active"
                            ? "#166534"
                            : "#374151",
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ fontSize: "14px" }}>
                    {row.apisSubscribed}
                  </TableCell>

                  <TableCell sx={{ fontSize: "14px" }}>
                    {formatNumber(row.totalCalls)}
                  </TableCell>

                  <TableCell
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color:
                        row.successRate >= 70
                          ? "#16A34A"
                          : "#F97316",
                    }}
                  >
                    {toIntPercent(row.successRate)}
                  </TableCell>

                  <TableCell sx={{ fontSize: "14px" }}>
                    {formatNumber(row.creditUsed)}
                  </TableCell>

                  <TableCell sx={{ fontSize: "14px" }}>
                    {formatDuration(row.avgResponseTimeMs)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, fontSize: "15px" }}>
                  No customers found
                </TableCell>
              </TableRow>
            )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};
